"use client";

import { Suggestion } from "../types";
import { cn } from "@/core/utils";
import { useActionsTrack } from "@/_old/features/analytics";
import { useAnalytics } from "@/_old/features/analytics";
import {
  EVENT_CATEGORIES,
  EVENTS,
} from "@/_old/features/analytics/constants/events";
import {
  ACTION_UPLOAD_PORTFOLIO,
  buildCategorizedGreetingSuggestions,
  CategorizedSuggestions,
  subscribeComparisonUploadLoading,
} from "../utils/chatbotUtils";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

interface SuggestionCardsProps {
  suggestions: Suggestion[];
  onSuggestionClick: (suggestion: Suggestion) => void;
  isGreeting?: boolean;
}

export function SuggestionCards({
  suggestions,
  onSuggestionClick,
  isGreeting = false,
}: SuggestionCardsProps) {
  const { onActionClick } = useActionsTrack();
  const { track } = useAnalytics();
  const [translate] = useTranslation(["core"]);
  const [isComparisonUploading, setIsComparisonUploading] = useState(false);

  useEffect(
    () => subscribeComparisonUploadLoading(setIsComparisonUploading),
    [],
  );

  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  const isSuggestionDisabled = (suggestion: Suggestion) =>
    suggestion.action === ACTION_UPLOAD_PORTFOLIO && isComparisonUploading;

  const getSuggestionLabel = (suggestion: Suggestion) => {
    if (
      suggestion.action === ACTION_UPLOAD_PORTFOLIO &&
      isComparisonUploading
    ) {
      return (
        <>
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />
          Enviando planilha...
        </>
      );
    }
    return suggestion.text;
  };

  const handleClick = (suggestion: Suggestion) => {
    if (isSuggestionDisabled(suggestion)) return;
    const trackingData = {
      suggestion_text: suggestion.text,
      suggestion_action: suggestion.action,
      suggestion_category: suggestion.category,
    };

    // if action is not empty, track the event
    if (suggestion.action) {
      const eventName = `agent_action_click_${suggestion.action}`;
      track(eventName, {
        category: EVENT_CATEGORIES.CHATBOT,
        value: trackingData,
      });
    } else {
      // if action is empty, use the category mapping with onActionClick
      const categoryMap: Record<string, keyof typeof EVENTS> = {
        reservas: "CHATBOT_SUGGESTION_BOOKINGS_CLICK",
        checkin: "CHATBOT_SUGGESTION_CHECKIN_CLICK",
        importação: "CHATBOT_SUGGESTION_IMPORT_MEMBERS_CLICK",
        importacao: "CHATBOT_SUGGESTION_IMPORT_MEMBERS_CLICK",
        "conta-acesso": "CHATBOT_SUGGESTION_ACCOUNT_ACCESS_CLICK",
        "espacos-reservas": "CHATBOT_SUGGESTION_SPACES_RESERVATIONS_CLICK",
        "gestao-relatorios": "CHATBOT_SUGGESTION_MANAGEMENT_REPORTS_CLICK",
      };

      const eventName =
        categoryMap[suggestion.category?.toLowerCase() || ""] ||
        "CHATBOT_SEND_MESSAGE";

      onActionClick(eventName, "CHATBOT", trackingData);
    }

    onSuggestionClick(suggestion);
  };

  const getConfirmButtonStyle = (suggestion: Suggestion) => {
    const text = suggestion.text?.toLowerCase() ?? "";
    const isPositiveAction =
      text.includes("adicionar") || text.includes("continuar");
    if (isPositiveAction) {
      return "bg-success-500 border-success-500 text-white hover:bg-success-600 hover:text-white hover:border-success-500 active:bg-success-700 active:text-white active:border-success-500 focus:ring-success-300";
    }
    return "bg-danger-500 border-danger-500 text-white hover:bg-danger-600 hover:text-white hover:border-danger-500 active:bg-danger-700 active:text-white active:border-danger-500 focus:ring-danger-300";
  };

  // if message is a greeting, render the categorized suggestions
  if (isGreeting) {
    const categorized = buildCategorizedGreetingSuggestions(
      (key: string) => translate(key) as string | undefined,
    );

    return (
      <div className="mt-3 space-y-4 px-3">
        {categorized.map((categoryGroup, categoryIndex) => (
          <div key={categoryIndex} className="space-y-2">
            <h3 className="text-sm font-medium text-darkgray-600 tracking-wide">
              {categoryGroup.categoryLabel}
            </h3>
            <div className="flex flex-wrap gap-2">
              {categoryGroup.suggestions.map((suggestion, index) => (
                <button
                  key={`${categoryIndex}-${index}`}
                  onClick={() => handleClick(suggestion)}
                  disabled={isSuggestionDisabled(suggestion)}
                  aria-busy={
                    suggestion.action === ACTION_UPLOAD_PORTFOLIO &&
                    isComparisonUploading
                  }
                  className={cn(
                    "px-5 py-2.5 text-sm rounded-[10px] border transition-colors font-semibold",
                    "bg-white border-[#b8b8b5] text-[#4e4d45] hover:bg-neutral-50 hover:border-[#999996]",
                    "focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-neutral-300",
                  )}
                  type="button"
                >
                  {getSuggestionLabel(suggestion)}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // default render for other messages (without categories)
  return (
    <div className="flex flex-wrap gap-2 mt-3 px-3">
      {suggestions.map((suggestion, index) => (
        <button
          key={index}
          onClick={() => handleClick(suggestion)}
          disabled={isSuggestionDisabled(suggestion)}
          aria-busy={
            suggestion.action === ACTION_UPLOAD_PORTFOLIO &&
            isComparisonUploading
          }
          className={cn(
            "px-5 py-2.5 text-sm rounded-[10px] border transition-colors font-semibold",
            suggestion.action === "confirm_action"
              ? getConfirmButtonStyle(suggestion)
              : "bg-white border-[#b8b8b5] text-[#4e4d45] hover:bg-neutral-50 hover:border-[#999996]",
            "focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-neutral-300",
          )}
          type="button"
        >
          {getSuggestionLabel(suggestion)}
        </button>
      ))}
    </div>
  );
}
